import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { SandboxNotice } from "@/components/SandboxNotice";
import { Button } from "@/components/ui/button";

/** The passenger stores a card for a trip booked by phone. Nothing is charged on this page. */
export default function SaveCard() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/public/card-request/${token}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(errMsg(e)));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Coming back from Stripe: keep asking until the card shows up on our side.
  useEffect(() => {
    if (!params.get("saved") || data?.saved) return;
    const tick = setInterval(() => {
      api.get(`/public/card-request/${token}/status`)
        .then(({ data: s }) => { if (s.saved) { clearInterval(tick); load(); } })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(tick);
  }, [params, data?.saved, token, load]);

  const start = async () => {
    setBusy(true); setError("");
    try {
      const { data: res } = await api.post(`/public/card-request/${token}/checkout`);
      window.location.href = res.checkout_url;
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  };

  if (error && !data) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink-900 px-6 text-center">
        <p data-testid="save-card-error" className="text-sm text-slate-400">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink-900">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }
  const waiting = params.get("saved") && !data.saved;

  return (
    <div data-testid="save-card-page" className="min-h-screen bg-ink-900 px-5 py-14">
      <div className="mx-auto max-w-lg">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
          {data.company.name}
        </p>
        <h1 data-testid="save-card-title" className="mt-3 font-display text-3xl text-slate-50">
          {data.saved ? "Your card is on file" : "Keep a card on file"}
        </h1>

        <div className="mt-8 rounded-xl border border-ink-600 bg-ink-800 p-5 sm:p-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Trip #{data.trip.number} · {data.trip.trip_date} {data.trip.pickup_time}
          </p>
          <p className="mt-2 text-sm text-slate-300">{data.trip.pickup}</p>
          <p className="text-sm text-slate-500">to {data.trip.dropoff}</p>
        </div>

        {data.saved ? (
          <div data-testid="save-card-done" className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
            <p className="flex items-center gap-2 text-sm text-emerald-300">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {data.brand || "Card"} ···· {data.last4} is saved for this trip.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-400">
              Anything still owed is settled on it about {data.grace_minutes} minutes after your trip,
              and you'll get a receipt by email. Call {data.company.phone || "the office"} any time to
              remove it.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-6 text-sm leading-relaxed text-slate-400">
              We'll hold your card securely with Stripe so the balance of your trip can be settled
              afterwards without another phone call. <span className="text-slate-200">Nothing is
              charged now</span>, and {data.company.name} never sees your card number.
            </p>
            {error && <p data-testid="save-card-checkout-error" className="mt-4 text-sm text-red-400">{error}</p>}
            {waiting && (
              <p data-testid="save-card-waiting" className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin text-gold" /> Confirming your card…
              </p>
            )}
            <Button data-testid="save-card-start" disabled={busy || !data.payments_ready} onClick={start}
              className="mt-6 h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><CreditCard className="mr-2 h-4 w-4" /> Add my card</>}
            </Button>
            <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold/70" />
              Card details are entered on Stripe's own secure page and stored by Stripe, not by us.
            </p>
            {data.sandbox && <div className="mt-4"><SandboxNotice testId="save-card-sandbox" /></div>}
          </>
        )}
      </div>
    </div>
  );
}
